// Duplicated and unmodified from progressive-diffs.ts to retain a version without new loading optimizations.
// Guarded with a feature flag `pr_react_loading_optimizations`
import {ProgressiveLoadingStatus, type ProgressiveDiffEntry} from '../../types/progressive-diff-types'
import {contextRangesForMarkers} from '../../utils/context-ranges-for-markers'
import {buildProgressiveDiffEntries} from '../../utils/build-progressive-diff-entries'
import {identifyDiffEntriesToLoad} from '../../utils/identify-diff-entries-to-load'
import {throttle} from '@github/mini-throttle'
import type {StateCreator} from 'zustand'
import type {DiffEntriesSlice} from './diff-entries'
import type {DiffSummariesSlice, DiffSummariesSliceOpts} from './diff-summaries'

export interface ProgressiveDiffsSlice {
  entries: ProgressiveDiffEntry[]
  selectedPathDigest: string | undefined

  // Public actions, intended for use outside the store.
  progressiveDiffsActions: {
    loadMore: (startingAt: ProgressiveDiffEntry, options?: {preferAnySoloEntry?: boolean}) => Promise<void>
    updateSelectedPathDigest: (pathDigest: string | undefined) => void
  }

  // Private actions, intended for use by public actions.
  internalProgressiveDiffsActions: {
    loadSelectedEntries: (selectedEntries: ProgressiveDiffEntry[]) => void | Promise<void>
  }
}

export type ProgressiveDiffSliceOpts = {
  prxFilesLiteThrottle?: boolean | undefined
  prxFilesMediumThrottle?: boolean | undefined
  prReactLoadingOptimizations?: boolean | undefined
  initialLoadedPathDigests: Set<string>
  selectedPathDigest: string | undefined
} & Pick<DiffSummariesSliceOpts, 'initialDiffSummaries'>

export const createProgressiveDiffsSlice =
  ({
    prxFilesLiteThrottle,
    prxFilesMediumThrottle,
    initialDiffSummaries,
    initialLoadedPathDigests,
    selectedPathDigest,
  }: ProgressiveDiffSliceOpts): StateCreator<
    ProgressiveDiffsSlice & DiffEntriesSlice & DiffSummariesSlice,
    [['zustand/devtools', never]],
    [],
    ProgressiveDiffsSlice
  > =>
  (set, get) => {
    let throttleTime: number

    if (prxFilesMediumThrottle) {
      throttleTime = 100
    } else if (prxFilesLiteThrottle) {
      throttleTime = 50
    } else {
      throttleTime = 0
    }

    const entries = buildProgressiveDiffEntries({
      diffSummaries: initialDiffSummaries,
      loadedPathDigests: initialLoadedPathDigests,
      loadingPathDigests: new Set<string>(),
      selectedPathDigest,
    })

    return {
      entries,
      selectedPathDigest,
      internalProgressiveDiffsActions: {
        loadSelectedEntries: throttle(async (selectedEntries: ProgressiveDiffEntry[]) => {
          if (selectedEntries.length === 0) return

          const pathsToFetch = selectedEntries.map(e => e.path)
          const pathDigestsToFetch = selectedEntries.map(e => e.pathDigest)

          const previouslyLoadedPathDigests = get()
            .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loaded)
            .map(e => e.pathDigest)
          const previouslyLoadingPathDigests = get()
            .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loading)
            .map(e => e.pathDigest)
          set(
            state => ({
              entries: buildProgressiveDiffEntries({
                diffSummaries: state.diffSummaries,
                loadedPathDigests: new Set(previouslyLoadedPathDigests),
                loadingPathDigests: new Set([...previouslyLoadingPathDigests, ...pathDigestsToFetch]),
                selectedPathDigest: state.selectedPathDigest,
              }),
            }),
            undefined,
            'loadSelectedEntries#preFetch',
          )

          const {loadDiffEntries} = get().diffEntriesActions
          const contextRanges = contextRangesForMarkers({
            paths: new Set(pathsToFetch),
            diffSummaries: get().diffSummaries,
          })
          const newlyLoadedDiffEntries = await loadDiffEntries({
            paths: pathsToFetch,
            contextRanges,
          })

          const noApiData = !newlyLoadedDiffEntries || newlyLoadedDiffEntries.length === 0
          if (noApiData) {
            const currentLoadedPathDigests = [
              ...get()
                .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loaded)
                .map(e => e.pathDigest),
            ]
            const currentLoadingPathDigests = get()
              .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loading)
              .map(e => e.pathDigest)

            return set(
              {
                entries: buildProgressiveDiffEntries({
                  diffSummaries: get().diffSummaries,
                  loadedPathDigests: new Set(currentLoadedPathDigests),
                  loadingPathDigests: new Set(currentLoadingPathDigests),
                  selectedPathDigest: get().selectedPathDigest,
                }),
              },
              undefined,
              'loadSelectedEntries#postFetch#noAPIData',
            )
          }

          const newlyLoadedPathDigests = newlyLoadedDiffEntries.map(ne => ne.pathDigest)
          const currentLoadedPathDigests = [
            ...get()
              .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loaded)
              .map(e => e.pathDigest),
            ...newlyLoadedPathDigests,
          ]
          const currentLoadingPathDigests = get()
            .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loading)
            .filter(e => !newlyLoadedPathDigests.includes(e.pathDigest))
            .map(e => e.pathDigest)

          set(
            (state: ProgressiveDiffsSlice) => ({
              ...state,
              entries: buildProgressiveDiffEntries({
                diffSummaries: get().diffSummaries,
                loadedPathDigests: new Set(currentLoadedPathDigests),
                loadingPathDigests: new Set(currentLoadingPathDigests),
                selectedPathDigest: get().selectedPathDigest,
              }),
            }),
            undefined,
            'loadSelectedEntries#postFetch',
          )
        }, throttleTime),
      },
      progressiveDiffsActions: {
        loadMore: async (
          startingAt: ProgressiveDiffEntry,
          {preferAnySoloEntry}: {preferAnySoloEntry?: boolean} = {},
        ) => {
          const entriesToLoad = identifyDiffEntriesToLoad({
            progressiveDiffEntries: get().entries,
            startingAt,
            preferAnySoloEntry,
          })

          return get().internalProgressiveDiffsActions.loadSelectedEntries(entriesToLoad)
        },
        updateSelectedPathDigest: (pathDigest: string | undefined) => {
          set((prevState: ProgressiveDiffsSlice) => ({
            ...prevState,
            selectedPathDigest: pathDigest,
          }))
          const selectedEntry = get().entries.find(e => e.pathDigest === pathDigest)
          if (selectedEntry) {
            return get().internalProgressiveDiffsActions.loadSelectedEntries([selectedEntry])
          }
        },
      },
    }
  }
